// Self-contained search service for the search feature.
// Imports only from within src/features/search/ and shared types.
// Changes to other features should NOT affect this module.

import { getSearchDoctorsWithBookingFields } from '../data/searchDoctorsData';
import { mapSearchCategoryToSlugs } from '../constants/searchCategoryMap';
import { searchCcsdEntries, type CcsdEntry } from '../data/ccsdCodes';
import { isCcsdSearchEnvironmentAllowed } from '../data/ccsdSearchGate';
import { searchSymptoms, type SymptomEntry } from '../data/symptoms';
import { SPEC_OPTION_TITLE_BY_SLUG } from '../constants/specialityOptions';
import type { DoctorCard } from '@/types/doctor';

export interface SearchParams {
  query?: string;
  speciality?: string | string[];
  subSpeciality?: string | string[];
  gender?: string | string[];
  type?: string;
  mainOnly?: boolean;
  limit?: number;
  offset?: number;
  bookingLocations?: string | string[];
  locations?: string | string[];
  languages?: string | string[];
  sortBy?: string;
}

export interface SearchResponse {
  items: DoctorCard[];
  total: number;
}

const SURGICAL_KEYWORDS = ['surgeon', 'surgery'];
const MIN_TOKEN_LENGTH = 1;

let cachedDoctors: DoctorCard[] | null = null;

/**
 * Resets the internal doctor cache. Exported for testability.
 * In production this is never called; tests use it to isolate state.
 */
export function resetSearchCache(): void {
  cachedDoctors = null;
}

async function getCachedDoctors(): Promise<DoctorCard[]> {
  if (!cachedDoctors) {
    cachedDoctors = (await getSearchDoctorsWithBookingFields()).sort(
      groupedSpecialistsSort,
    );
  }
  return cachedDoctors;
}

// ─── Filter Predicate Helpers (SRP) ──────────────────────────────────────────

function matchesSurgical(item: DoctorCard): boolean {
  return !!(
    item.heading?.toLowerCase().includes('surgeon') ||
    item.description?.toLowerCase().includes('surgeon') ||
    item.primarySpeciality?.toLowerCase().includes('surgery') ||
    (item.specialities ?? []).some(
      (s) =>
        s.toLowerCase().includes('surgery') ||
        SURGICAL_KEYWORDS.some((kw) => s.toLowerCase().includes(kw)),
    )
  );
}

function matchesSurgeonType(item: DoctorCard): boolean {
  return !!(
    item.heading?.toLowerCase().includes('surgeon') ||
    item.description?.toLowerCase().includes('surgeon') ||
    (item.specialities ?? []).some(
      (s) =>
        s.toLowerCase().includes('surgeon') ||
        s.toLowerCase().includes('surgery'),
    )
  );
}

function matchesConsultantType(item: DoctorCard): boolean {
  return !!(
    item.description?.toLowerCase().includes('consultant') ||
    item.heading?.toLowerCase().includes('consultant')
  );
}

function matchesPhysiotherapistType(item: DoctorCard): boolean {
  const desc = item.description?.toLowerCase() ?? '';
  const primary = item.primarySpeciality?.toLowerCase() ?? '';
  const specs = (item.specialities ?? []).map((s) => s.toLowerCase());
  return (
    desc.includes('physiotherapist') ||
    primary === 'physiotherapy' ||
    specs.some((s) => s === 'physiotherapy')
  );
}

function matchesRadiologistType(item: DoctorCard): boolean {
  const desc = item.description?.toLowerCase() ?? '';
  const primary = item.primarySpeciality?.toLowerCase() ?? '';
  const specs = (item.specialities ?? []).map((s) => s.toLowerCase());
  return (
    desc.includes('radiologist') ||
    primary.includes('radiology') ||
    specs.some((s) => s.includes('radiology'))
  );
}

function matchesAnaesthetistType(item: DoctorCard): boolean {
  const desc = item.description?.toLowerCase() ?? '';
  const primary = item.primarySpeciality?.toLowerCase() ?? '';
  const specs = (item.specialities ?? []).map((s) => s.toLowerCase());
  return (
    desc.includes('anaesthetist') ||
    primary === 'anaesthetics' ||
    specs.some((s) => s === 'anaesthetics')
  );
}

function matchesDoctorType(item: DoctorCard, type: string): boolean {
  if (!type) return true;
  switch (type) {
    case 'surgeon':
      return matchesSurgeonType(item);
    case 'consultant':
      return matchesConsultantType(item);
    case 'physiotherapist':
      return matchesPhysiotherapistType(item);
    case 'radiologist':
      return matchesRadiologistType(item);
    case 'anaesthetist':
      return matchesAnaesthetistType(item);
    default:
      return true;
  }
}

function hideExcludedTypesUnlessSelected(
  item: DoctorCard,
  type: string,
  hasSpecialityFilter: boolean,
): boolean {
  // Explicit speciality searches already restrict results to matching doctors.
  if (hasSpecialityFilter) return true;
  if (type === 'radiologist' || type === 'anaesthetist') return true;
  return !matchesRadiologistType(item) && !matchesAnaesthetistType(item);
}

function matchesQuery(item: DoctorCard, tokens: string[]): boolean {
  if (tokens.length === 0) return true;

  const name = (item.fullName ?? '').toLowerCase();
  const primarySpec = (item.primarySpeciality ?? '').toLowerCase();
  const heading = (item.heading ?? '').toLowerCase();
  const description = (item.description ?? '').toLowerCase();
  const specs = (item.specialities ?? []).map((s) => s.toLowerCase());
  const keywords = (item.metaKeywords ?? []).map((k) => k.toLowerCase());

  return tokens.every((token) => {
    return (
      name.includes(token) ||
      primarySpec.includes(token) ||
      heading.includes(token) ||
      description.includes(token) ||
      specs.some((spec) => spec.includes(token)) ||
      keywords.some((kw) => kw.includes(token))
    );
  });
}

function normalizeSpeciality(value: string): string {
  return value.toLowerCase().trim().replace(/[-_]/g, ' ');
}

function specialityMatch(docSpeciality: string, filterValue: string): boolean {
  const normDoc = normalizeSpeciality(docSpeciality);
  const normFilter = normalizeSpeciality(filterValue);
  const normDocWithHyphens = normDoc.replace(/\s+/g, '-');
  const normFilterWithHyphens = normFilter.replace(/\s+/g, '-');
  return normDoc === normFilter || normDocWithHyphens === normFilterWithHyphens;
}

function matchesSpeciality(
  item: DoctorCard,
  selectedSpecs: string[],
  mainOnly: boolean,
): boolean {
  if (selectedSpecs.length === 0) return true;

  return selectedSpecs.some((specVal) => {
    const target = specVal.toLowerCase().trim();

    if (target === 'surgical speciality' || target === 'surgical-speciality') {
      return matchesSurgical(item);
    }

    const primary = item.primarySpeciality ?? '';

    return (
      specialityMatch(primary, specVal) ||
      (!mainOnly &&
        (item.specialities ?? []).some((s) => specialityMatch(s, specVal)))
    );
  });
}

function matchesSubSpeciality(
  item: DoctorCard,
  selectedSubSpecs: string[],
  mainOnly: boolean,
): boolean {
  if (mainOnly || selectedSubSpecs.length === 0) return true;

  return selectedSubSpecs.some((subVal) => {
    return (item.specialities ?? []).some((s) => specialityMatch(s, subVal));
  });
}

function matchesGender(item: DoctorCard, selectedGenders: string[]): boolean {
  if (selectedGenders.length === 0) return true;
  const docGender = (item.gender ?? '').toLowerCase();
  return selectedGenders.includes(docGender);
}

function matchesBookingLocations(
  item: DoctorCard,
  _selectedBookingLocs: string[],
): boolean {
  if (_selectedBookingLocs.length === 0) return true;
  if (!item.isBookable) return false;
  return true;
}

function matchesLocations(item: DoctorCard, selectedLocs: string[]): boolean {
  if (selectedLocs.length === 0) return true;

  return (
    item.primaryLocations?.some((loc) =>
      selectedLocs.some((sel) => loc.heading.toLowerCase().includes(sel)),
    ) ||
    item.privateLocations?.some((loc) =>
      selectedLocs.some((sel) => loc.heading.toLowerCase().includes(sel)),
    ) ||
    false
  );
}

function matchesLanguages(item: DoctorCard, selectedLangs: string[]): boolean {
  if (selectedLangs.length === 0) return true;
  if (!item.languages || item.languages.length === 0) return false;

  const docLangs = item.languages.map((l) => l.toLowerCase());

  return docLangs.some((lang) => selectedLangs.some((sel) => lang === sel));
}

// ─── Sorting Strategy Registry (Strategy Pattern / OCP) ─────────────────────

type SortStrategy = (a: DoctorCard, b: DoctorCard) => number;

const groupedSpecialistsCollator = new Intl.Collator(undefined, {
  sensitivity: 'base',
});

const getSpecialistCategory = (doctor: DoctorCard) =>
  doctor.primarySpeciality || doctor.specialities?.[0] || 'Other';

const getSpecialistLastName = (doctor: DoctorCard) =>
  (doctor.fullName ?? '').trim().split(' ').at(-1) ?? '';

const groupedSpecialistsSort: SortStrategy = (a, b) => {
  const categoryComparison = groupedSpecialistsCollator.compare(
    getSpecialistCategory(a),
    getSpecialistCategory(b),
  );

  if (categoryComparison !== 0) return categoryComparison;

  return groupedSpecialistsCollator.compare(
    getSpecialistLastName(a),
    getSpecialistLastName(b),
  );
};

const sortStrategies: Record<string, SortStrategy> = {
  'name-asc': (a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''),
  'name-desc': (a, b) => (b.fullName ?? '').localeCompare(a.fullName ?? ''),
  'price-asc': (a, b) => {
    const pA = parseFloat(a.primaryPrice || '999999') || 999999;
    const pB = parseFloat(b.primaryPrice || '999999') || 999999;
    return pA - pB;
  },
  'price-desc': (a, b) => {
    const pA = parseFloat(a.primaryPrice || '0') || 0;
    const pB = parseFloat(b.primaryPrice || '0') || 0;
    return pB - pA;
  },
};

// ─── Main Search Orchestration ───────────────────────────────────────────────

export async function searchDoctors(
  params: SearchParams = {},
): Promise<SearchResponse> {
  const specialists = await getCachedDoctors();

  const query = params.query?.toLowerCase().trim() || '';
  const mainOnly = params.mainOnly !== false;

  const parseListParam = (val?: string | string[]): string[] => {
    if (!val) return [];
    return (Array.isArray(val) ? val : val.split(','))
      .filter(Boolean)
      .map((x) => x.trim().toLowerCase());
  };

  const selectedGenders = parseListParam(params.gender);
  const selectedBookingLocs = parseListParam(params.bookingLocations);
  const selectedLocs = parseListParam(params.locations);
  const selectedLangs = parseListParam(params.languages);

  const selectedSpecs: string[] = [];
  if (params.speciality) {
    const rawSpecs = Array.isArray(params.speciality)
      ? params.speciality
      : params.speciality.split(',').filter(Boolean);

    rawSpecs.forEach((spec) => {
      const lower = spec.toLowerCase().trim();
      if (lower === 'general') return;
      if (lower === 'surgical speciality' || lower === 'surgical-speciality') {
        selectedSpecs.push(spec);
      } else {
        selectedSpecs.push(...mapSearchCategoryToSlugs(spec));
      }
    });
  }

  const selectedSubSpecs = parseListParam(params.subSpeciality);

  const queryTokens = query
    ? query.split(/\s+/).filter((t) => t.length >= MIN_TOKEN_LENGTH)
    : [];

  // CCSD codes & Symptom mappings:
  // When the query matches known CCSD codes or UK symptoms (e.g. "W303", "sciatica", "tinnitus", "heartburn"),
  // match doctors via the mapped specialities and condition keywords.
  // CCSD code matching follows the feature flag for this environment
  // (runtime override, env-scoped registry default).
  // (Symptom matching is a separate, long-standing feature and is unaffected.)
  const ccsdEnabled = await isCcsdSearchEnvironmentAllowed();
  const matchingCcsdEntries: CcsdEntry[] =
    ccsdEnabled && query ? searchCcsdEntries(query) : [];
  const matchingSymptoms: SymptomEntry[] = query ? searchSymptoms(query) : [];

  const mappedSpecialities = new Set<string>([
    ...matchingCcsdEntries.flatMap((e) => e.specialities),
    ...matchingSymptoms.flatMap((s) => s.specialities),
  ]);

  const mappedSpecTitleWords = new Set<string>(
    Array.from(mappedSpecialities).flatMap(
      (slug) =>
        SPEC_OPTION_TITLE_BY_SLUG[slug]?.toLowerCase().match(/[a-z0-9]+/g) ??
        [],
    ),
  );

  const ccsdTreatmentWords = matchingCcsdEntries.flatMap(
    (e) =>
      e.treatment
        .toLowerCase()
        .match(/[a-z0-9]+/g)
        ?.filter((w) => w.length > 2) ?? [],
  );

  const symptomSynonymWords = matchingSymptoms.flatMap((s) => [
    s.symptom.toLowerCase(),
    ...s.synonyms.map((syn) => syn.toLowerCase()),
  ]);

  const matchesQueryOrCcsdOrSymptom = (item: DoctorCard): boolean => {
    if (matchesQuery(item, queryTokens)) return true;
    if (mappedSpecialities.size === 0) return false;

    const primary = item.primarySpeciality ?? '';
    const specs = (item.specialities ?? []).map((s) => s.toLowerCase());

    const matchesMappedSpeciality = Array.from(mappedSpecialities).some(
      (slug) =>
        specialityMatch(primary, slug) ||
        specs.some((s) => specialityMatch(s, slug)),
    );
    if (matchesMappedSpeciality) return true;

    const haystack = [
      item.fullName,
      item.heading,
      item.description,
      primary,
      ...specs,
      ...(item.metaKeywords ?? []),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (!haystack) return false;

    const matchesSpecTitle = Array.from(mappedSpecTitleWords).some(
      (w) => w.length > 2 && haystack.includes(w),
    );
    if (matchesSpecTitle) return true;

    if (ccsdTreatmentWords.some((w) => w.length > 3 && haystack.includes(w))) {
      return true;
    }

    return symptomSynonymWords.some(
      (syn) => syn.length > 3 && haystack.includes(syn),
    );
  };

  const filterPipeline = [
    matchesQueryOrCcsdOrSymptom,
    (item: DoctorCard) => matchesSpeciality(item, selectedSpecs, mainOnly),
    (item: DoctorCard) =>
      matchesSubSpeciality(item, selectedSubSpecs, mainOnly),
    (item: DoctorCard) => matchesGender(item, selectedGenders),
    (item: DoctorCard) => matchesBookingLocations(item, selectedBookingLocs),
    (item: DoctorCard) => matchesLocations(item, selectedLocs),

    (item: DoctorCard) => matchesLanguages(item, selectedLangs),
    (item: DoctorCard) => matchesDoctorType(item, params.type ?? ''),
    (item: DoctorCard) =>
      hideExcludedTypesUnlessSelected(
        item,
        params.type ?? '',
        selectedSpecs.length > 0,
      ),
  ];

  const filtered = specialists.filter((item) =>
    filterPipeline.every((predicate) => predicate(item)),
  );

  if (params.sortBy && sortStrategies[params.sortBy]) {
    filtered.sort(sortStrategies[params.sortBy]);
  }

  const total = filtered.length;

  let items = filtered;
  if (params.offset !== undefined || params.limit !== undefined) {
    const offset = params.offset || 0;
    const limit = params.limit !== undefined ? params.limit : filtered.length;
    items = filtered.slice(offset, offset + limit);
  }

  return { items, total };
}
